import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Container,
  Link,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Box,
  Snackbar,
  Alert,
  GlobalStyles,
} from '@mui/material';
import { ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Autocomplete } from '@mui/material';
import { useTheme, useMediaQuery } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { format, parseISO } from 'date-fns';
import { debounce } from 'lodash';
import { MAPBOX_ACCESS_TOKEN } from '../mapboxConfig';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';

interface JobApplication {
  id: string;
  company: string;
  position: string;
  location: string;
  salary: string;
  salaryType?: 'yearly' | 'hourly'; // Made optional
  hoursPerWeek?: number;
  status: 'Applied' | 'Interviewing' | 'Hired' | 'Rejected';
  dateapplied: string;
  link?: string;
}

interface ApiResponse {
  applications: JobApplication[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

const ApplicationsList: React.FC = () => {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingApp, setEditingApp] = useState<JobApplication | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [sortField, setSortField] = useState<string>('company');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // const hasJobLinks = applications.some(app => app.link);

  const fetchSuggestions = async (query: string) => {
    const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json`;
    const params = new URLSearchParams({
      access_token: MAPBOX_ACCESS_TOKEN || '',
      types: 'place',
      limit: '5',
    });

    const response = await fetch(`${endpoint}?${params}`);
    const data = await response.json();
    return data.features.map((feature: any) => feature.place_name);
  };

  const debouncedFetchSuggestions = useCallback(
    debounce(async (input: string) => {
      if (input.length > 2) {
        const suggestions = await fetchSuggestions(input);
        setLocationSuggestions(suggestions);
      } else {
        setLocationSuggestions([]);
      }
    }, 300),
    []
  );

  // Function to parse salary and infer salaryType if not provided
  const inferSalaryType = (salary: string): 'yearly' | 'hourly' => {
    // Check for '/hr' or 'hour' in the salary string
    if (salary.toLowerCase().includes('/hr') || salary.toLowerCase().includes('hour')) {
      return 'hourly';
    }

    // Infer based on numeric value: <1000 as hourly, >=1000 as yearly
    const numericSalary = parseFloat(salary.replace(/[^0-9.]/g, ''));
    return numericSalary < 1000 ? 'hourly' : 'yearly';
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications`, {
        headers: {
          'x-auth-token': localStorage.getItem('token') || '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch applications');
      }

      const data: ApiResponse = await response.json();
      console.log('Fetched applications:', data.applications); // Add this for debugging

      const applicationsWithSalaryType = data.applications.map((app) => ({
        ...app,
        salaryType: app.salaryType || inferSalaryType(app.salary),
      }));

      setApplications(applicationsWithSalaryType);
      setTotalCount(data.totalCount);
      setTotalPages(data.totalPages);
      setCurrentPage(data.currentPage);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to load applications. Please try again.');
      setApplications([]);
      setLoading(false);
    }
  };

  const getStatusColor = (status: JobApplication['status']) => {
    switch (status) {
      case 'Applied':
        return 'default';
      case 'Interviewing':
        return 'primary';
      case 'Hired':
        return 'success';
      case 'Rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleEdit = (app: JobApplication) => {
    const updatedApp = { ...app };
    if (!updatedApp.salaryType) {
      updatedApp.salaryType = inferSalaryType(updatedApp.salary);
      if (updatedApp.salaryType === 'hourly') {
        updatedApp.salary = updatedApp.salary.replace('/hr', '').trim();
      }
    }
    setEditingApp(updatedApp);
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this application?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${id}`, {
          method: 'DELETE',
          headers: {
            'x-auth-token': localStorage.getItem('token') || '',
          },
        });
        if (!response.ok) {
          throw new Error('Failed to delete application');
        }
        fetchApplications(); // Refresh the list after deletion
        setSnackbarMessage('Application deleted successfully!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (error) {
        console.error('Error deleting application:', error);
        setSnackbarMessage(
          error instanceof Error
            ? error.message
            : 'An error occurred while deleting the application'
        );
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setEditingApp(null);
  };

  const handleSave = async () => {
    if (editingApp) {
      const updatedApp = {
        ...editingApp,
        dateapplied: editingApp.dateapplied ? new Date(editingApp.dateapplied).toISOString() : null,
        salary:
          editingApp.salaryType === 'hourly'
            ? `${editingApp.salary}`
            : editingApp.salary,
      };
      try {
        console.log('Sending updated application:', updatedApp);
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications/${updatedApp.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'x-auth-token': localStorage.getItem('token') || '',
          },
          body: JSON.stringify(updatedApp),
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to update application: ${errorData.message || response.statusText}`);
        }
        const data = await response.json();
        console.log('Update response:', data);
        fetchApplications(); // Refresh the list after update
        handleDialogClose();
        setSnackbarMessage('Application updated successfully!');
        setSnackbarSeverity('success');
        setOpenSnackbar(true);
      } catch (error) {
        console.error('Error updating application:', error);
        setSnackbarMessage(
          error instanceof Error
            ? error.message
            : 'An error occurred while updating the application'
        );
        setSnackbarSeverity('error');
        setOpenSnackbar(true);
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditingApp(prev =>
      prev
        ? {
          ...prev,
          [name]: name === 'hoursPerWeek' ? Number(value) : value,
        }
        : null
    );
    if (name === 'location') {
      debouncedFetchSuggestions(value);
    }
  };

  const handleLocationChange = (event: any, value: string | null) => {
    const location = value === 'Remote' || value === 'N/A' ? value : value || '';
    setEditingApp(prev => (prev ? { ...prev, location } : null));
  };

  const handleStatusChange = (e: SelectChangeEvent<JobApplication['status']>) => {
    setEditingApp(prev => (prev ? { ...prev, status: e.target.value as JobApplication['status'] } : null));
  };

  const fieldToSortFieldMap: { [key: string]: string } = {
    'Company': 'company',
    'Position': 'position',
    'Location': 'location',
    'Yearly Salary': 'yearlysalary',
    'Hourly Salary': 'hourlysalary',
    'Status': 'status',
    'Date Applied': 'dateapplied',
    'Job Link': '',
    'Actions': '',
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const parseSalaryValue = (salary: string): number => {
    const numericString = salary.replace(/[^0-9.]/g, '');
    const value = parseFloat(numericString);
    return isNaN(value) ? 0 : value;
  };

  const getNumericSalary = (app: JobApplication, salaryType: 'yearlysalary' | 'hourlysalary'): number => {
    if (salaryType === 'yearlysalary') {
      if (app.salaryType === 'yearly') {
        return parseSalaryValue(app.salary);
      }
    } else if (salaryType === 'hourlysalary') {
      if (app.salaryType === 'hourly') {
        return parseSalaryValue(app.salary);
      }
    }
    return 0;
  };

  const sortedApplications = [...applications].sort((a, b) => {
    if (sortField === 'yearlysalary' || sortField === 'hourlysalary') {
      const aSalary = getNumericSalary(a, sortField);
      const bSalary = getNumericSalary(b, sortField);
      return sortDirection === 'asc' ? aSalary - bSalary : bSalary - aSalary;
    }
    if (sortField in a && sortField in b) {
      const aValue = (a as any)[sortField];
      const bValue = (b as any)[sortField];
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
    }
    return 0;
  });

  const formatDateForInput = (dateString: string) => {
    if (!dateString) return '';
    const date = parseISO(dateString);
    return format(date, 'yyyy-MM-dd');
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = parseISO(dateString);
      return format(date, 'MM/dd/yyyy');
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return 'Invalid Date';
    }
  };

  const handleSalaryTypeChange = (e: SelectChangeEvent<'yearly' | 'hourly'>) => {
    setEditingApp(prev => (prev ? { ...prev, salaryType: e.target.value as 'yearly' | 'hourly' } : null));
  };

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  return (
    <Container maxWidth="lg">
      {/* GlobalStyles for SimpleBar scrollbar customization */}
      <GlobalStyles
        styles={{
          '.simplebar-scrollbar:before': {
            background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
          },
          '.simplebar-scrollbar.simplebar-visible:before': {
            opacity: 1,
          },
          '.simplebar-track.simplebar-horizontal .simplebar-scrollbar': {
            height: '8px',
          },
          '.simplebar-track.simplebar-vertical .simplebar-scrollbar': {
            width: '8px',
          },
          '.simplebar-content-wrapper': {
            scrollBehavior: 'smooth',
          },
        }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Header */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography
          variant={isMobile ? 'h5' : 'h4'}
          component="h1"
          gutterBottom
          sx={{ color: '#3a7bd5' }}
        >
          Job Applications
        </Typography>
      </Box>

      {/* Content */}
      {loading ? (
        <Typography>Loading applications...</Typography>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <SimpleBar
            style={{ maxHeight: isMobile ? 'calc(100vh - 200px)' : 'none' }}
            forceVisible="x"
            autoHide={false}
          >
            <Table size={isMobile ? 'small' : 'medium'}>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(to right, #3a7bd5, #00d2ff)' }}>
                  {['Company', 'Position', 'Location', 'Yearly Salary', 'Hourly Salary', 'Status', 'Date Applied'].map((field) => (
                    <TableCell
                      key={field}
                      sx={{
                        color: 'white',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                        whiteSpace: 'nowrap',
                        padding: isMobile ? '8px 4px' : '16px',
                      }}
                      onClick={() => {
                        const sortFieldKey = fieldToSortFieldMap[field];
                        if (sortFieldKey) {
                          handleSort(sortFieldKey);
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        {isMobile ? field.charAt(0) : field}
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
                          <ArrowUpward
                            sx={{
                              fontSize: 18,
                              opacity: sortField === fieldToSortFieldMap[field] && sortDirection === 'asc' ? 1 : 0.3,
                              transition: 'opacity 0.2s',
                            }}
                          />
                          <ArrowDownward
                            sx={{
                              fontSize: 18,
                              opacity: sortField === fieldToSortFieldMap[field] && sortDirection === 'desc' ? 1 : 0.3,
                              transition: 'opacity 0.2s',
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedApplications.map((app) => (
                  <TableRow key={app.id} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' } }}>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      {app.link ? (
                        <Link href={app.link} target="_blank" rel="noopener noreferrer" sx={{ color: '#3a7bd5', textDecoration: 'none' }}>
                          {app.company}
                        </Link>
                      ) : (
                        app.company
                      )}
                    </TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>{app.position}</TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>{app.location}</TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      {app.salaryType === 'yearly' && app.salary ? `$${app.salary}` : '--'}
                    </TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      {app.salaryType === 'hourly' && app.salary ? `$${app.salary}/hr` : '--'}
                    </TableCell>
                    <TableCell sx={{ padding: isMobile ? '8px 4px' : '16px' }}>
                      <Chip label={app.status} color={getStatusColor(app.status)} size={isMobile ? 'small' : 'medium'} />
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: isMobile ? '8px 4px' : '16px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ flexGrow: 1 }}>{formatDate(app.dateapplied)}</Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                        <IconButton
                          onClick={() => handleEdit(app)}
                          size="small"
                          sx={{ color: '#3a7bd5', p: 0.5 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(app.id)}
                          size="small"
                          sx={{ color: '#ff4081', p: 0.5, mt: 0.5 }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </SimpleBar>
        </Paper>
      )}

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ background: 'linear-gradient(to right, #3a7bd5, #00d2ff)', color: 'white' }}>
          Edit Application
        </DialogTitle>
        <DialogContent>
          {editingApp && (
            <Box sx={{ pt: 2 }}>
              {/* Company */}
              <TextField
                fullWidth
                margin="normal"
                label="Company"
                name="company"
                value={editingApp.company}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              {/* Position */}
              <TextField
                fullWidth
                margin="normal"
                label="Position"
                name="position"
                value={editingApp.position}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              {/* Location */}
              <Autocomplete
                freeSolo
                options={['Remote', 'N/A', ...locationSuggestions]}
                value={editingApp.location}
                onInputChange={(event, newInputValue) => {
                  if (newInputValue !== 'Remote' && newInputValue !== 'N/A') {
                    debouncedFetchSuggestions(newInputValue);
                  }
                }}
                onChange={handleLocationChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    margin="normal"
                    label="Location"
                    name="location"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                )}
              />

              {/* Salary */}
              <TextField
                fullWidth
                margin="normal"
                label="Salary"
                name="salary"
                value={editingApp.salary}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                helperText={editingApp.salaryType === 'hourly' ? 'Enter hourly rate' : 'Enter yearly salary'}
              />

              {/* Salary Type */}
              <FormControl fullWidth margin="normal">
                <InputLabel>Salary Type</InputLabel>
                <Select
                  name="salaryType"
                  value={editingApp.salaryType || ''}
                  onChange={handleSalaryTypeChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="yearly">Yearly</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                </Select>
              </FormControl>

              {/* Hours per Week (only if hourly) */}
              {editingApp.salaryType === 'hourly' && (
                <TextField
                  fullWidth
                  margin="normal"
                  label="Hours per Week"
                  name="hoursPerWeek"
                  type="number"
                  value={editingApp.hoursPerWeek || ''}
                  onChange={handleInputChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}

              {/* Status */}
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={editingApp.status}
                  onChange={handleStatusChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="Applied">Applied</MenuItem>
                  <MenuItem value="Interviewing">Interviewing</MenuItem>
                  <MenuItem value="Hired">Hired</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>

              {/* Date Applied */}
              <TextField
                fullWidth
                margin="normal"
                label="Date Applied"
                name="dateapplied"
                type="date"
                value={formatDateForInput(editingApp.dateapplied)}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />

              {/* Job Link */}
              <TextField
                fullWidth
                margin="normal"
                label="Job Link"
                name="link"
                value={editingApp.link || ''}
                onChange={handleInputChange}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} sx={{ color: '#ff4081' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            sx={{
              background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(to right, #3a7bd5, #3a7bd5)',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ApplicationsList;
